export const EXAMPLE_THREAD_ROUTE_PREFIX = 'ot_example' as const;
export const EXAMPLE_HELP_ACCESS_DURATION_MS = 24 * 60 * 60 * 1000;
/** Seeds created for new workspaces, in seed order. */
export const EXAMPLE_WORKSPACE_THREAD_SEED_IDS = [
  'how-imai-can-help',
  'design-floral-earrings',
  'ganni-brand-import',
  'kidsuper-collection',
  'kkr-target-list',
  'ganni-nb-tech-pack',
  'bearbottom-seo-audit',
] as const;

/**
 * Seeds from the retired ideation bundle. Existing workspaces still hold rows
 * stamped with these ids, so their routes stay reserved and their stamps stay
 * valid, but new workspaces never receive them.
 */
export const LEGACY_EXAMPLE_WORKSPACE_THREAD_SEED_IDS = [
  'ideate-sneakers',
  'ideate-dresses',
  'ideate-jewelry',
] as const;

export const ALL_EXAMPLE_WORKSPACE_THREAD_SEED_IDS = [
  ...EXAMPLE_WORKSPACE_THREAD_SEED_IDS,
  ...LEGACY_EXAMPLE_WORKSPACE_THREAD_SEED_IDS,
] as const;

export type ExampleWorkspaceThreadSeedId =
  (typeof EXAMPLE_WORKSPACE_THREAD_SEED_IDS)[number];

export type LegacyExampleWorkspaceThreadSeedId =
  (typeof LEGACY_EXAMPLE_WORKSPACE_THREAD_SEED_IDS)[number];

/** Any seed id a stored org-thread row may carry. */
export type StoredExampleWorkspaceThreadSeedId =
  (typeof ALL_EXAMPLE_WORKSPACE_THREAD_SEED_IDS)[number];

export function exampleWorkspaceThreadRouteIdForSeed(args: {
  organizationId: string;
  seedId: StoredExampleWorkspaceThreadSeedId;
}): string {
  const workspaceId = args.organizationId.trim();
  if (!workspaceId) throw new Error('Example workspace route requires an org');
  return `${EXAMPLE_THREAD_ROUTE_PREFIX}-${args.seedId}-${workspaceId}`;
}

export function exampleWorkspaceHelpThreadRouteId(
  organizationId: string
): string {
  return exampleWorkspaceThreadRouteIdForSeed({
    organizationId,
    seedId: 'how-imai-can-help',
  });
}

export function isReservedExampleWorkspaceThreadRoute(args: {
  organizationId: string;
  routeId: string | null | undefined;
}): boolean {
  return ALL_EXAMPLE_WORKSPACE_THREAD_SEED_IDS.some(
    (seedId) =>
      args.routeId ===
      exampleWorkspaceThreadRouteIdForSeed({
        organizationId: args.organizationId,
        seedId,
      })
  );
}

export function isProtectedExampleWorkspaceHelpThread(args: {
  organizationId: string;
  routeId: string | null | undefined;
  exampleThreadSeedId: string | null | undefined;
}): boolean {
  return (
    args.exampleThreadSeedId === 'how-imai-can-help' &&
    args.routeId === exampleWorkspaceHelpThreadRouteId(args.organizationId)
  );
}

export function canDeleteExampleWorkspaceThread(args: {
  organizationId: string;
  routeId: string | null | undefined;
  exampleThreadSeedId: string | null | undefined;
}): boolean {
  return !isProtectedExampleWorkspaceHelpThread(args);
}

export function canRenameExampleWorkspaceThread(args: {
  organizationId: string;
  routeId: string | null | undefined;
  exampleThreadSeedId: string | null | undefined;
}): boolean {
  if (!args.exampleThreadSeedId) return true;
  if (
    !ALL_EXAMPLE_WORKSPACE_THREAD_SEED_IDS.some(
      (seedId) => seedId === args.exampleThreadSeedId
    )
  ) {
    return true;
  }
  return !isReservedExampleWorkspaceThreadRoute(args);
}

export function isExampleHelpAccessActive(
  workspaceCreatedAt: number,
  now = Date.now()
): boolean {
  const age = now - workspaceCreatedAt;
  return age >= 0 && age < EXAMPLE_HELP_ACCESS_DURATION_MS;
}
